import type { ReactNode } from "react";

import { cn } from "@/lib/helpers/cn";
import { COSIGNATURE_ACKNOWLEDGEMENT } from "@/features/cosignature/constants";
import type { CosignatureDocumentType, UUID } from "@/core/types";
import { testLocator } from "@/lib/helpers/test-locator";
import { TextArea2 } from "@/ui2/atoms/TextArea";
import { useAddendumCosignMeta } from "@/features/cosignature/api/queries/useAddendumCosignMeta";
import { useNoteCosignMeta } from "@/features/cosignature/api/queries/useNoteCosignMeta";

import { formatSignatureTimestamp } from "./helpers/format-signature-timestamp";
import { nameWithCredentials } from "./helpers/name-with-credentials";
import { NoteCosignature } from "./components/NoteCosignature";
import { NoteSignature } from "./components/NoteSignature";
import { ProviderSignature } from "./components/ProviderSignature";

type SignatureListProps = {
  className?: string;
  cosignatureBadge?: ReactNode;
  cosignatureTimestamp?: Nullable<string>;
  cosignedBy?: UUID;
  credentials?: string | null;
  documentId?: string;
  documentType?: CosignatureDocumentType;
  isCosignRequired?: boolean;
  isNoteComplete?: boolean;
  providerPractice?: string | null;
  signature?: string | null;
  signatureDate?: string | null;
  signatureTimestamp?: Nullable<string>;
  testId?: string;
};

export function SignatureList({
  className,
  cosignatureBadge,
  cosignatureTimestamp,
  cosignedBy,
  credentials,
  documentId,
  documentType,
  isCosignRequired,
  isNoteComplete,
  providerPractice,
  signature,
  signatureDate,
  signatureTimestamp,
  testId = "note-footer",
}: SignatureListProps) {
  const isAddendum = documentType === "note_addendum";
  const canFetchMeta = isCosignRequired && isNoteComplete && !!documentId;

  // documentType decides which of the two endpoints applies; both hooks are always
  // called (rules of hooks — SignatureList is mounted per-item in list contexts like
  // PrintNoteView) but mutually exclusive via `enabled`.
  const { data: noteMeta } = useNoteCosignMeta({
    documentId: documentId ?? "",
    enabled: !isAddendum && canFetchMeta,
  });
  const { data: addendumMeta } = useAddendumCosignMeta({
    documentId: documentId ?? "",
    enabled: isAddendum && canFetchMeta,
  });
  const cosignatureMeta = isAddendum ? addendumMeta : noteMeta;
  const formattedSignedTimestamp = formatSignatureTimestamp(signatureTimestamp);
  const signedDate = formattedSignedTimestamp ?? signatureDate;
  const isCosigned = isCosignRequired && isNoteComplete && !!cosignedBy && !cosignatureBadge;
  const cosignedTimestamp = cosignatureTimestamp ?? cosignatureMeta?.cosignature_date;
  const cosignedDate = isCosigned ? formatSignatureTimestamp(cosignedTimestamp) : undefined;

  return (
    <footer
      className={cn(
        className,
        "tw:-mx-6 tw:border-t-2 tw:border-dashed tw:border-stroke-quaternary tw:px-6 tw:pt-6",
      )}
      {...testLocator(testId)}
    >
      <div className="tw:grid tw:break-inside-avoid tw:grid-cols-2 tw:items-start tw:gap-6">
        <ul className="tw:flex tw:min-w-0 tw:flex-col tw:gap-4">
          {signature && (
            <NoteSignature label="Signature" {...testLocator("providerSignature")}>
              <ProviderSignature>
                {nameWithCredentials({ credentials, name: signature })}
              </ProviderSignature>
            </NoteSignature>
          )}

          {signature && signedDate && (
            <NoteSignature label="Signed">
              {formattedSignedTimestamp ? (
                <time dateTime={signatureTimestamp ?? undefined}>{signedDate}</time>
              ) : (
                signedDate
              )}
            </NoteSignature>
          )}

          {providerPractice && (
            <NoteSignature label="Provider Practice">{providerPractice}</NoteSignature>
          )}
        </ul>

        <ul className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
          {isCosignRequired && (
            <>
              <NoteCosignature cosignatureBadge={cosignatureBadge} cosignedBy={cosignedBy} />

              {isNoteComplete && (
                <li className="tw:text-xs tw:text-text-secondary">{COSIGNATURE_ACKNOWLEDGEMENT}</li>
              )}

              {cosignatureMeta?.acknowledgment_details && (
                <li className="tw:flex tw:border-t tw:border-stroke-quaternary tw:pt-0.5">
                  <TextArea2
                    aria-label="Additional Comments"
                    className="tw:bg-transparent tw:px-0"
                    id={`cosignature-acknowledgment-details-${documentId}`}
                    mode="display"
                    value={cosignatureMeta.acknowledgment_details}
                  />
                </li>
              )}

              {cosignedDate && (
                <NoteSignature label="Co-signed">
                  <time dateTime={cosignedTimestamp}>{cosignedDate}</time>
                </NoteSignature>
              )}
            </>
          )}
        </ul>
      </div>
    </footer>
  );
}
